/**
 * B2's cache (`KLQ-20260819-006`).
 *
 * **Nothing polls and nothing subscribes.** A firm's staff changes when somebody on this
 * screen changes it, so each mutation invalidates the one list it touched and that is the
 * entire invalidation story (CLAUDE.md, data fetching).
 *
 * **`workspaceMembersQueryOptions` lives here and `features/people` imports it.** The same
 * resource was already being read by C10's add-a-colleague picker under the key
 * `['workspace', 'members']`; a second key for one resource is two answers to one question,
 * and the second answer goes stale silently. The query moved to this feature rather than the
 * other way round because this is the screen that *owns* the firm's people — the picker
 * borrows them.
 *
 * The one non-obvious edge: re-roling **yourself** changes what you may do on this screen and
 * everywhere else, so it invalidates the session. Re-roling anybody else cannot — authority is
 * per person — and invalidating on every change would refetch the session for nothing on the
 * common path.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SESSION_QUERY_KEY, useActiveWorkspaceId } from '@/features/session';

import type { WorkspaceRole } from './api';
import {
  changeWorkspaceMemberRole,
  createWorkspaceFirm,
  createWorkspaceFirmContact,
  deleteWorkspaceFirmContact,
  listFirmContactsOnProjects,
  listWorkspaceFirmContacts,
  listWorkspaceFirms,
  listWorkspaceInvitations,
  listWorkspaceMembers,
  removeWorkspaceMember,
  revokeWorkspaceInvitation,
  sendMemberPasswordReset,
  sendWorkspaceInvitation,
  setMemberPassword,
  updateWorkspaceFirm,
  updateWorkspaceFirmContact,
} from './api';

/**
 * Everything B2 reads about one of the caller's firms hangs off that firm's key.
 *
 * **The id is load-bearing** (`KLQ-20260819-007` §6). `GET /workspace/members` is singular and
 * takes no id in the path — it answers whichever company the request acted as — so a key
 * without the company would show studio A's employees under studio B's heading on the first
 * render after a switch. The header and the key move together, or the cache lies.
 */
function workspaceKey(workspaceId: string) {
  return ['workspace', workspaceId] as const;
}

export function workspaceMembersQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceKey(workspaceId), 'members'] as const,
    queryFn: () => listWorkspaceMembers(),
    // Reference data: a firm's staff list changes when somebody is hired or leaves.
    staleTime: 5 * 60_000,
  });
}

export function workspaceInvitationsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceKey(workspaceId), 'invitations'] as const,
    queryFn: () => listWorkspaceInvitations(),
    staleTime: 60_000,
  });
}

export function useWorkspaceMembers(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...workspaceMembersQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
}

export function useWorkspaceInvitations() {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...workspaceInvitationsQueryOptions(workspaceId ?? ''),
    enabled: workspaceId !== null,
  });
}

export function useSendWorkspaceInvitation() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? '';
  return useMutation({
    mutationFn: (body: { readonly email: string; readonly role: WorkspaceRole }) =>
      sendWorkspaceInvitation(body),
    // A `409` on a retry earns the same `409`; a `403` from the trust ladder is a decision,
    // not a transient failure. Neither is worth a second send.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceInvitationsQueryOptions(workspaceId).queryKey,
      });
    },
  });
}

export function useRevokeWorkspaceInvitation() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? '';
  return useMutation({
    mutationFn: (invitationId: string) => revokeWorkspaceInvitation(invitationId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceInvitationsQueryOptions(workspaceId).queryKey,
      });
    },
  });
}

export interface WorkspaceRoleChange {
  readonly membershipId: string;
  readonly role: WorkspaceRole;
  /** Whether the person being re-roled is the caller. See the module note. */
  readonly isSelf: boolean;
}

export function useChangeWorkspaceMemberRole() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? '';
  return useMutation({
    mutationFn: ({ membershipId, role }: WorkspaceRoleChange) =>
      changeWorkspaceMemberRole(membershipId, role),
    retry: false,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: workspaceMembersQueryOptions(workspaceId).queryKey,
      });
      if (variables.isSelf) {
        void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      }
    },
  });
}

export function useRemoveWorkspaceMember() {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? '';
  return useMutation({
    mutationFn: (membershipId: string) => removeWorkspaceMember(membershipId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceMembersQueryOptions(workspaceId).queryKey,
      });
    },
  });
}

/**
 * The two credential acts (`KLQ-20260821-002` §E), and the two things they deliberately do
 * **not** do.
 *
 * **Neither invalidates anything.** A password is not a field of `MemberResponse`, and
 * nothing else on the row moves either — the colleague keeps their name, their address, their
 * role and their membership. Refetching the list to show the same rows back would be a
 * request spent on confirming that nothing changed (CLAUDE.md, data fetching).
 *
 * **Neither retries.** `retry: false` is the house rule for a mutation here, and these two
 * are the strongest case for it: a repeated reset supersedes the link it just sent and mails
 * the colleague twice, and a repeated set writes a second audit event and a second notice for
 * one administrator's single decision. A failure that a retry could fix is not a failure
 * these endpoints produce.
 */
export function useSendMemberPasswordReset() {
  return useMutation({
    mutationFn: (membershipId: string) => sendMemberPasswordReset(membershipId),
    retry: false,
  });
}

export interface MemberPasswordChoice {
  readonly membershipId: string;
  readonly newPassword: string;
}

export function useSetMemberPassword() {
  return useMutation({
    mutationFn: ({ membershipId, newPassword }: MemberPasswordChoice) =>
      setMemberPassword(membershipId, newPassword),
    retry: false,
  });
}

// --- The firm directory (`KLQ-20260820-007`, owner 2026-09-16) -------------------------------
//
// **Reference data, under the workspace's key** — the same shape the task types have and for the
// same reason: three surfaces read this list (studio settings, the project roster's *add from
// the directory*, the transmittal picker's contacts) and a per-surface copy would be three
// requests for one truth. It changes when somebody edits it here, so nothing polls it and every
// mutation below invalidates exactly the two entries (CLAUDE.md, data fetching).
//
// The contacts are **one query for the whole directory**, never one per firm: the screen draws
// each firm's people under its row, and a hook keyed by firm would fire a request per row.

export function workspaceFirmsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceKey(workspaceId), 'firms'] as const,
    queryFn: () => listWorkspaceFirms(),
    staleTime: 5 * 60_000,
  });
}

export function workspaceFirmContactsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceKey(workspaceId), 'firm-contacts'] as const,
    queryFn: () => listWorkspaceFirmContacts(),
    staleTime: 5 * 60_000,
  });
}

export function useWorkspaceFirms(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...workspaceFirmsQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
}

/**
 * The people the caller's **projects** wrote down under the studio's firms.
 *
 * Its own entry under the workspace key, not under a project's: the directory reads it whole
 * and the answer spans every project the caller is on. The same five minutes as the two lists
 * beside it — a contact changes when somebody edits it, which is not while a screen is open.
 */
export function firmContactsOnProjectsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: [...workspaceKey(workspaceId), 'firm-contacts-on-projects'] as const,
    queryFn: () => listFirmContactsOnProjects(),
    staleTime: 5 * 60_000,
  });
}

export function useFirmContactsOnProjects(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...firmContactsOnProjectsQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
}

export function useWorkspaceFirmContacts(enabled = true) {
  const workspaceId = useActiveWorkspaceId();
  return useQuery({
    ...workspaceFirmContactsQueryOptions(workspaceId ?? ''),
    enabled: enabled && workspaceId !== null,
  });
}

/**
 * Every write to the directory sweeps the same two entries, and — for a rename — **the project
 * surfaces too**.
 *
 * A firm renamed here is renamed on every project it is on, because the roster row holds a
 * reference rather than a name. The roster a person is looking at would otherwise keep showing
 * the old spelling until something else happened to refetch it, which is the exact drift the
 * reference was chosen to end.
 */
function useDirectoryMutation<TArgs, TResult>(
  run: (args: TArgs) => Promise<TResult>,
  { sweepProjects = false }: { readonly sweepProjects?: boolean } = {},
) {
  const queryClient = useQueryClient();
  const workspaceId = useActiveWorkspaceId() ?? '';
  return useMutation({
    mutationFn: run,
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKey(workspaceId) });
      if (sweepProjects) void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useCreateFirm() {
  return useDirectoryMutation(createWorkspaceFirm);
}

export function useUpdateFirm() {
  return useDirectoryMutation(
    ({
      firmId,
      ...body
    }: {
      readonly firmId: string;
      readonly name?: string;
      readonly note?: string | null;
      readonly address?: string | null;
      readonly registration_id?: string | null;
      readonly tax_id?: string | null;
      readonly vat_id?: string | null;
      readonly phone?: string | null;
      readonly email?: string | null;
      readonly primary_contact_id?: string | null;
      readonly archived?: boolean;
    }) => updateWorkspaceFirm(firmId, body),
    { sweepProjects: true },
  );
}

export function useCreateFirmContact() {
  return useDirectoryMutation(createWorkspaceFirmContact);
}

export function useUpdateFirmContact() {
  return useDirectoryMutation(
    ({
      contactId,
      ...body
    }: {
      readonly contactId: string;
      readonly name?: string;
      readonly phone?: string | null;
      readonly email?: string | null;
    }) => updateWorkspaceFirmContact(contactId, body),
  );
}

export function useDeleteFirmContact() {
  return useDirectoryMutation((contactId: string) => deleteWorkspaceFirmContact(contactId));
}
